import ReturnsNullOn404 from "common/http/returnsNullOn404";
import ThrowsValidationError from "common/http/throwsValidationError";
import { Partner } from "features/settings/importMapping/partner";
import BidIntegration from "./bidIntegration";
import ErpIntegration from "domain/erpIntegration";
import PartnerIntegration from "domain/partnerIntegration";
import AdminHttpService from "admin/common/adminHttpService";

export interface ManualImportRequest {
    type: ManualImportType;
    recordIds: string[];
    dataTypes: ImportDataType[];
    importBeforeDate?: Date;
    importAfterDate?: Date;
    createRecord?: boolean;
    customArguments?: any;
}

export enum ManualImportType {
    ByDate = 1,
    SpecificRecords,
    ForCustomers,
    Custom,
    ForCustomerBalance
}

export enum ImportDataType {
    Contract = 1,
    Account,
    CommodityBalance,
    ScaleTicket,
    Settlement
}

export interface CreatePartnerIntegrationRequest {
    partner: Partner;
    externalId: string;
    authenticationKey?: string;
    configuration?: any;
}

export interface UpdatePartnerIntegrationRequest {
    authenticationKey?: string;
    configuration?: any;
}

class PartnerIntegrationService extends AdminHttpService {
    
    async listPartnerIntegrations(merchantId: string): Promise<PartnerIntegration[]> {
        const response = await this.getClient(merchantId).get<PartnerIntegration[]>(`/offers/v1/merchants/${merchantId}/partners`);
        return response.data;
    }

    @ThrowsValidationError()
    async createPartnerIntegration(merchantId: string, request: CreatePartnerIntegrationRequest): Promise<PartnerIntegration> {
        const response = await this.getClient(merchantId).post<PartnerIntegration>(`/offers/v1/merchants/${merchantId}/partners`, request);
        return response.data;
    }

    @ThrowsValidationError()
    async updatePartnerIntegration(merchantId: string, partner: Partner, request: UpdatePartnerIntegrationRequest): Promise<any> {
        await this.getClient(merchantId).put(`/offers/v1/merchants/${merchantId}/partners/${partner}`, request);
    }

    async deletePartnerIntegration(merchantId: string, partner: Partner): Promise<any> {
        await this.getClient(merchantId).delete(`/offers/v1/merchants/${merchantId}/partners/${partner}`);
    }
    
    @ReturnsNullOn404()
    async getErpIntegration(merchantId: string): Promise<ErpIntegration> {
        const response = await this.getClient(merchantId).get<ErpIntegration>(`/offers/v1/merchants/${merchantId}/erp-integration`);
        return response.data;
    }

    @ThrowsValidationError()
    async updateErpIntegration(merchantId: string, integration: ErpIntegration): Promise<any> {
        await this.getClient(merchantId).put<ErpIntegration>(`/offers/v1/merchants/${merchantId}/erp-integration`, integration);
    }

    async deleteErpIntegration(merchantId: string): Promise<any> {
        await this.getClient(merchantId).delete(`/offers/v1/merchants/${merchantId}/erp-integration`);
    }
    
    @ReturnsNullOn404()
    async getBidIntegration(merchantId: string): Promise<BidIntegration> {
        const response = await this.getClient(merchantId).get<BidIntegration>(`/offers/v1/merchants/${merchantId}/bid-integration`);
        return response.data;
    }

    @ThrowsValidationError()
    async updateBidIntegration(merchantId: string, integration: BidIntegration): Promise<any> {
        await this.getClient(merchantId).put(`/offers/v1/merchants/${merchantId}/bid-integration`, {
            partner: integration.partner,
            isEnabled: integration.isEnabled,
            bidConfiguration: integration.bidConfiguration
        });
    }

    async deleteBidIntegration(merchantId: string): Promise<any> {
        await this.getClient(merchantId).delete(`/offers/v1/merchants/${merchantId}/bid-integration`);
    }

    @ThrowsValidationError()
    async performManualErpImport(merchantId: string, request: ManualImportRequest): Promise<any> {
        await this.getClient(merchantId).post(`/offers/v1/merchants/${merchantId}/erp-integration/manual-import`, request);
    }

    async importBids(merchantId: string): Promise<any> {
        await this.getClient(merchantId).post(`/offers/v1/merchants/${merchantId}/bid-integration/import`);
    }

    async importMappingData(merchantId: string, partner: Partner): Promise<any> {
        await this.getClient(merchantId).post(`/offers/v1/merchants/${merchantId}/partners/${partner}/import-mappings`, {});
    }
}

const partnerIntegrationService = new PartnerIntegrationService();

export default partnerIntegrationService;